import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { GewerkId } from './gewerkeKatalog';
import {
    allgemeineHinweise,
    analysiere,
    ausschussListe,
    baueMatrix,
    baueZeitachse,
    nichtZugeordnete,
    type AbgleichErgebnis,
    type GewerkAusPlan,
    type KoordinierungPaar,
} from './koordinierungLogic';
import { fetchKoordinierungsLage, type PlanQuelle } from '../../services/koordinierungService';
import type { UeberschneidungsPaar } from './types';

/**
 * Laedt die Lage der Gewerke-Ueberschneidung und haelt sie beisammen.
 *
 * Legt bewusst NICHTS an: das blosse Oeffnen der Kachel darf keinen Datensatz
 * erzeugen. Dieselbe Ueberlegung wie in `usePlanung.ts` und beim Schreibschutz
 * fuer Nur-Ansicht-Projekte, wo Selbstheilungs-Migrationen beim reinen Lesen
 * geschrieben hatten.
 *
 * Gerechnet wird HIER nichts — alle Ableitungen kommen aus
 * `koordinierungLogic.ts` und sind dort ohne DOM geprueft (es gibt kein jsdom).
 * Der Hook haelt nur den Zustand, merkt sich die Zuordnungen von Hand und
 * spielt geaenderte Zeilen zurueck.
 */

/**
 * Zuordnung eines nicht erkannten Freitext-Gewerks auf ein Katalog-Gewerk.
 *
 * ═══ WARUM DIE ZUORDNUNG NICHT GESPEICHERT WIRD ══════════════════════════
 * Sie lebt in dieser Sitzung und wird beim naechsten Oeffnen erneut angeboten.
 * Das ist eine bewusste Entscheidung und kein vergessener Schreibweg:
 *
 *   • Die Spalte dafuer waere `handbuch_gewerke.gewerk_norm`. Diese Tabelle
 *     und ihr Dienst gehoeren zum SiGe-Handbuch; ein zweiter Schreibweg
 *     dorthin ergaebe zwei Herren ueber dieselbe Spalte.
 *   • Im Browser abzulegen waere schlimmer als gar nicht: der Schluessel eines
 *     Paares haengt am Wortlaut im Plan, nicht an der Zuordnung
 *     (`gewerkSchluessel`). Waere es andersherum, saehe der naechste Leser —
 *     ohne die Ablage des ersten — dessen bestaetigte Zeilen als „überholt"
 *     und dieselbe Paarung daneben noch einmal als „unbewertet".
 *
 * Die Zuordnung entscheidet deshalb nur, WELCHE Katalogregeln zu einem Paar
 * vorgeschlagen werden. Was daraus bestaetigt wird, steht anschliessend als
 * eigene Zeile in der Datenbank und ueberlebt jede Sitzung.
 */
export type Zuordnungen = Record<string, GewerkId>;

export interface KoordinierungsLageImClient {
    quelle: PlanQuelle;
    paare: KoordinierungPaar[];
    tabelleFehlt: boolean;
}

const LEER: KoordinierungsLageImClient = {
    quelle: { sigeProject: null, arbeitsschritte: [] },
    paare: [],
    tabelleFehlt: false,
};

export const useKoordinierung = (projectId: string | null) => {
    const [lage, setLage] = useState<KoordinierungsLageImClient>(LEER);
    const [zuordnungen, setZuordnungen] = useState<Zuordnungen>({});
    const [laden, setLaden] = useState(false);
    const [fehler, setFehler] = useState<string | null>(null);

    // Lauf-Zaehler gegen spaete Antworten: wechselt der Anwender das Projekt,
    // waehrend die erste Abfrage noch laeuft, darf deren Ergebnis die
    // inzwischen geladene zweite Lage nicht ueberschreiben.
    const lauf = useRef(0);

    const neuLaden = useCallback(async () => {
        const meiner = ++lauf.current;
        if (!projectId || projectId === 'temp-id') {
            setLage(LEER);
            setLaden(false);
            setFehler(null);
            return;
        }
        setLaden(true);
        setFehler(null);
        try {
            const geladen = await fetchKoordinierungsLage(projectId);
            if (meiner !== lauf.current) return;
            setLage(geladen);
        } catch (e) {
            if (meiner !== lauf.current) return;
            setFehler(e instanceof Error ? e.message : String(e));
        } finally {
            if (meiner === lauf.current) setLaden(false);
        }
    }, [projectId]);

    useEffect(() => { void neuLaden(); }, [neuLaden]);

    // Der Projektwechsel nimmt die Zuordnungen mit — sie gehoeren zu den
    // Gewerksnamen EINES Projekts und haetten in einem anderen keinen Sinn.
    useEffect(() => { setZuordnungen({}); }, [projectId]);

    /** Eine geaenderte oder neue Bewertung in die Liste zurueckspielen, ohne
     *  alles neu zu laden — sonst springt die Ansicht bei jeder Bestaetigung. */
    const uebernehmePaar = useCallback((paar: KoordinierungPaar) => {
        setLage(alt => ({
            ...alt,
            paare: alt.paare.some(p => p.id === paar.id)
                ? alt.paare.map(p => (p.id === paar.id ? paar : p))
                : [...alt.paare, paar],
        }));
    }, []);

    const entfernePaar = useCallback((id: string) => {
        setLage(alt => ({ ...alt, paare: alt.paare.filter(p => p.id !== id) }));
    }, []);

    /** Setzt Felder an mehreren Zeilen auf einmal — fuer das Kennzeichnen als
     *  überholt, das viele Zeilen in einem Zug trifft. */
    const uebernehmeFelder = useCallback((ids: string[], felder: Partial<KoordinierungPaar>) => {
        if (!ids || !ids.length) return;
        const menge = new Set(ids);
        setLage(alt => ({
            ...alt,
            paare: alt.paare.map(p => (menge.has(p.id) ? { ...p, ...felder } : p)),
        }));
    }, []);

    const setzeZuordnung = useCallback((norm: string, gewerk: GewerkId | null) => {
        setZuordnungen(alt => {
            const neu = { ...alt };
            if (gewerk) neu[norm] = gewerk; else delete neu[norm];
            return neu;
        });
    }, []);

    // ─── Ableitungen ─────────────────────────────────────────────────────
    // Alles aus der puren Logik; hier steht nur das Zwischenspeichern.

    const analyse = useMemo(
        () => analysiere(lage.quelle.arbeitsschritte, lage.paare, zuordnungen),
        [lage.quelle.arbeitsschritte, lage.paare, zuordnungen],
    );

    const gewerke: GewerkAusPlan[] = analyse.gewerke;
    const paare: UeberschneidungsPaar[] = analyse.paare;
    const ergebnis: AbgleichErgebnis = analyse.ergebnis;

    const matrix = useMemo(() => baueMatrix(gewerke, ergebnis.zeilen), [gewerke, ergebnis.zeilen]);
    const zeitachse = useMemo(
        () => baueZeitachse(
            gewerke,
            paare,
            lage.quelle.sigeProject ? lage.quelle.sigeProject.zeitraum_von : null,
            lage.quelle.sigeProject ? lage.quelle.sigeProject.zeitraum_bis : null,
        ),
        [gewerke, paare, lage.quelle.sigeProject],
    );
    const ohneZeitraum = useMemo(() => ausschussListe(gewerke), [gewerke]);
    const offeneGewerke = useMemo(() => nichtZugeordnete(gewerke), [gewerke]);
    const hinweise = useMemo(() => allgemeineHinweise(gewerke, paare), [gewerke, paare]);

    return {
        quelle: lage.quelle,
        gespeichert: lage.paare,
        tabelleFehlt: lage.tabelleFehlt,
        laden,
        fehler,
        neuLaden,
        uebernehmePaar,
        entfernePaar,
        uebernehmeFelder,
        zuordnungen,
        setzeZuordnung,
        gewerke,
        paare,
        ergebnis,
        matrix,
        zeitachse,
        ohneZeitraum,
        offeneGewerke,
        hinweise,
    };
};
